import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { service } from '@ember/service';
import type DocsService from '../services/kolay/docs.ts';
import type { Collection, Page } from '../services/kolay/types.ts';
import type RouterService from '@ember/routing/router-service';
export class PageNav extends Component<{
    /**
   * The `<nav>` element. It has a default `aria-label` of "Selected Group".
   * Normally an `aria-label` is not required,
   * but when there are multiple `<nav>` elements on a screen, it is required.
   */ Element: HTMLElement;
    Args: {
        /**
     * The class to apply to the `<a>` element when its link is active.
     *
     * Defaults to "active"
     */ activeClass?: string;
    };
    Blocks: {
        /**
     * If provided, this block will yield back the page for customizing the name.
     * By default the `name` property will be used.
     *
     * Example:
     * ```gjs
     * import { PageNav } from 'kolay/components';
     *
     * function toSentenceCase(name) { /* ... \*\/ }
     *
     * <template>
     *   <PageNav>
     *     <:page as |page|>
     *       {{toSentenceCase page.name}}
     *     </:page>
     *   </PageNav>
     * </template>
     * ```
     */ page: [Page];
        /**
     * If provided, this block will yield back the collection for customizing the name.
     * By default the `name` property will be used.
     *
     * Example:
     * ```gjs
     * import { PageNav } from 'kolay/components';
     *
     * function toSentenceCase(name) { /* ... \*\/ }
     *
     * <template>
     *   <PageNav>
     *     <:collection as |collection|>
     *       {{toSentenceCase collection.name}}
     *     </:collection>
     *   </PageNav>
     * </template>
     * ```
     */ collection: [Collection];
    };
}> {
    @service('kolay/docs')
    docs: DocsService;
    /**
   * Ember doesn't yet have a way to forward blocks,
   * so we have  to do this weird manualy forwarding ourselves
   *
   * This is extra annoying since Pages is a recursive component.
   */ static{
        template(`
    <nav aria-label='Selected Group' ...attributes>
      <Pages @item={{this.docs.tree}}>

        <:page as |p|>
          {{#if (has-block 'page')}}
            {{yield p to='page'}}
          {{else}}
            {{p.name}}
          {{/if}}
        </:page>

        <:collection as |c|>
          {{#if (has-block 'collection')}}
            {{yield c to='collection'}}
          {{else}}
            {{c.name}}
          {{/if}}
        </:collection>

      </Pages>
    </nav>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
function isCollection(x1: Page | Collection): x is Collection {
    return 'pages' in x1;
}
let Pages = class Pages extends Component<{
    Args: {
        item: Page | Collection;
        activeClass?: string;
    };
    Blocks: {
        page: [Page];
        collection: [Collection];
    };
}> {
    @service
    router: RouterService;
    get activeClass() {
        return this.args.activeClass ?? 'active';
    }
    isActive = (subPath1: string)=>{
        if (subPath1 === '/') return false;
        return this.router.currentURL?.startsWith(subPath1);
    };
    static{
        template(`
    <ul>
      {{#if (isCollection @item)}}
        {{#each @item.pages as |page|}}
          <li>
            {{#if (isCollection page)}}
              {{yield page to='collection'}}
            {{/if}}

            <Pages @item={{page}}>
              <:page as |p|>{{yield p to='page'}}</:page>
              <:collection as |c|>{{yield c to='collection'}}</:collection>
            </Pages>
          </li>
        {{/each}}
      {{else}}
        <a
          href={{@item.path}}
          class={{if (this.isActive @item.path) this.activeClass}}
        >{{yield @item to='page'}}</a>
      {{/if}}
    </ul>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
};
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbIi92ZXJjZWwvcGF0aDAvdWkvc3JjL2NvbXBvbmVudHMvcGFnZS1uYXYuZ3RzIl0sInNvdXJjZXNDb250ZW50IjpbImltcG9ydCBDb21wb25lbnQgZnJvbSAnQGdsaW1tZXIvY29tcG9uZW50JztcbmltcG9ydCB7IHNlcnZpY2UgfSBmcm9tICdAZW1iZXIvc2VydmljZSc7XG5cbmltcG9ydCB0eXBlIERvY3NTZXJ2aWNlIGZyb20gJy4uL3NlcnZpY2VzL2tvbGF5L2RvY3MudHMnO1xuaW1wb3J0IHR5cGUgeyBDb2xsZWN0aW9uLCBQYWdlIH0gZnJvbSAnLi4vc2VydmljZXMva29sYXkvdHlwZXMudHMnO1xuaW1wb3J0IHR5cGUgUm91dGVyU2VydmljZSBmcm9tICdAZW1iZXIvcm91dGluZy9yb3V0ZXItc2VydmljZSc7XG5cbmV4cG9ydCBjbGFzcyBQYWdlTmF2IGV4dGVuZHMgQ29tcG9uZW50PHtcbiAgLyoqXG4gICAqIFRoZSBgPG5hdj5gIGVsZW1lbnQuIEl0IGhhcyBhIGRlZmF1bHQgYGFyaWEtbGFiZWxgIG9mIFwiU2VsZWN0ZWQgR3JvdXBcIi5cbiAgICogTm9ybWFsbHkgYW4gYGFyaWEtbGFiZWxgIGlzIG5vdCByZXF1aXJlZCxcbiAgICogYnV0IHdoZW4gdGhlcmUgYXJlIG11bHRpcGxlIGA8bmF2PmAgZWxlbWVudHMgb24gYSBzY3JlZW4sIGl0IGlzIHJlcXVpcmVkLlxuICAgKi9cbiAgRWxlbWVudDogSFRNTEVsZW1lbnQ7XG4gIEFyZ3M6IHtcbiAgICAvKipcbiAgICAgKiBUaGUgY2xhc3MgdG8gYXBwbHkgdG8gdGhlIGA8YT5gIGVsZW1lbnQgd2hlbiBpdHMgbGluayBpcyBhY3RpdmUuXG4gICAgICpcbiAgICAgKiBEZWZhdWx0cyB0byBcImFjdGl2ZVwiXG4gICAgICovXG4gICAgYWN0aXZlQ2xhc3M_OiBzdHJpbmc7XG4gIH07XG4gIEJsb2Nrczoge1xuICAgIC8qKlxuICAgICAqIElmIHByb3ZpZGVkLCB0aGlzIGJsb2NrIHdpbGwgeWllbGQgYmFjayB0aGUgcGFnZSBmb3IgY3VzdG9taXppbmcgdGhlIG5hbWUuXG4gICAgICogQnkgZGVmYXVsdCB0aGUgYG5hbWVgIHByb3BlcnR5IHdpbGwgYmUgdXNlZC5cbiAgICAgKlxuICAgICAqIEV4YW1wbGU6XG4gICAgICogYGBgZ2pzXG4gICAgICogaW1wb3J0IHsgUGFnZU5hdiB9IGZyb20gJ2tvbGF5L2NvbXBvbmVudHMnO1xuICAgICAqXG4gICAgICogZnVuY3Rpb24gdG9TZW50ZW5jZUNhc2UobmFtZSkgeyAvKiAuLi4gXFwqXFwvIH1cbiAgICAgKlxuICAgICAqIDx0ZW1wbGF0ZT5cbiAgICAgKiAgIDxQYWdlTmF2PlxuICAgICAqICAgICA8OnBhZ2UgYXMgfHBhZ2V8PlxuICAgICAqICAgICAgIHt7dG9TZW50ZW5jZUNhc2UgcGFnZS5uYW1lfX1cbiAgICAgKiAgICAgPC86cGFnZT5cbiAgICAgKiAgIDwvUGFnZU5hdj5cbiAgICAgKiA8L3RlbXBsYXRlPlxuICAgICAqIGBgYFxuICAgICAqL1xuICAgIHBhZ2U6IFtQYWdlXTtcbiAgICAvKipcbiAgICAgKiBJZiBwcm92aWRlZCwgdGhpcyBibG9jayB3aWxsIHlpZWxkIGJhY2sgdGhlIGNvbGxlY3Rpb24gZm9yIGN1c3RvbWl6aW5nIHRoZSBuYW1lLlxuICAgICAqIEJ5IGRlZmF1bHQgdGhlIGBuYW1lYCBwcm9wZXJ0eSB3aWxsIGJlIHVzZWQuXG4gICAgICpcbiAgICAgKiBFeGFtcGxlOlxuICAgICAqIGBgYGdqc1xuICAgICAqIGltcG9ydCB7IFBhZ2VOYXYgfSBmcm9tICdrb2xheS9jb21wb25lbnRzJztcbiAgICAgKlxuICAgICAqIGZ1bmN0aW9uIHRvU2VudGVuY2VDYXNlKG5hbWUpIHsgLyogLi4uIFxcKlxcLyB9XG4gICAgICpcbiAgICAgKiA8dGVtcGxhdGU-XG4gICAgICogICA8UGFnZU5hdj5cbiAgICAgKiAgICAgPDpjb2xsZWN0aW9uIGFzIHxjb2xsZWN0aW9ufD5cbiAgICAgKiAgICAgICB7e3RvU2VudGVuY2VDYXNlIGNvbGxlY3Rpb24ubmFtZX19XG4gICAgICogICAgIDwvOmNvbGxlY3Rpb24-XG4gICAgICogICA8L1BhZ2VOYXY-XG4gICAgICogPC90ZW1wbGF0ZT5cbiAgICAgKiBgYGBcbiAgICAgKi9cbiAgICBjb2xsZWN0aW9uOiBbQ29sbGVjdGlvbl07XG4gIH07XG59PiB7XG4gIEBzZXJ2aWNlKCdrb2xheS9kb2NzJykgZGVjbGFyZSBkb2NzOiBEb2NzU2VydmljZTtcblxuICAvKipcbiAgICogRW1iZXIgZG9lc24ndCB5ZXQgaGF2ZSBhIHdheSB0byBmb3J3YXJkIGJsb2NrcyxcbiAgICogc28gd2UgaGF2ZSAgdG8gZG8gdGhpcyB3ZWlyZCBtYW51YWx5IGZvcndhcmRpbmcgb3Vyc2VsdmVzXG4gICAqXG4gICAqIFRoaXMgaXMgZXh0cmEgYW5ub3lpbmcgc2luY2UgUGFnZXMgaXMgYSByZWN1cnNpdmUgY29tcG9uZW50LlxuICAgKi9cbiAgPHRlbXBsYXRlPlxuICAgIDxuYXYgYXJpYS1sYWJlbD0nU2VsZWN0ZWQgR3JvdXAnIC4uLmF0dHJpYnV0ZXM-XG4gICAgICA8UGFnZXMgQGl0ZW09e3t0aGlzLmRvY3MudHJlZX19PlxuXG4gICAgICAgIDw6cGFnZSBhcyB8cHw-XG4gICAgICAgICAge3sjaWYgKGhhcy1ibG9jayAncGFnZScpfX1cbiAgICAgICAgICAgIHt7eWllbGQgcCB0bz0ncGFnZSd9fVxuICAgICAgICAgIHt7ZWxzZX19XG4gICAgICAgICAgICB7e3AubmFtZX19XG4gICAgICAgICAge3svaWZ9fVxuICAgICAgICA8LzpwYWdlPlxuXG4gICAgICAgIDw6Y29sbGVjdGlvbiBhcyB8Y3w-XG4gICAgICAgICAge3sjaWYgKGhhcy1ibG9jayAnY29sbGVjdGlvbicpfX1cbiAgICAgICAgICAgIHt7eWllbGQgYyB0bz0nY29sbGVjdGlvbid9fVxuICAgICAgICAgIHt7ZWxzZX19XG4gICAgICAgICAgICB7e2MubmFtZX19XG4gICAgICAgICAge3svaWZ9fVxuICAgICAgICA8Lzpjb2xsZWN0aW9uPlxuXG4gICAgICA8L1BhZ2VzPlxuICAgIDwvbmF2PlxuICA8L3RlbXBsYXRlPlxufVxuXG5mdW5jdGlvbiBpc0NvbGxlY3Rpb24oeDogUGFnZSB8IENvbGxlY3Rpb24pOiB4IGlzIENvbGxlY3Rpb24ge1xuICByZXR1cm4gJ3BhZ2VzJyBpbiB4O1xufVxuXG5jbGFzcyBQYWdlcyBleHRlbmRzIENvbXBvbmVudDx7XG4gIEFyZ3M6IHsgaXRlbTogUGFnZSB8IENvbGxlY3Rpb247IGFjdGl2ZUNsYXNzPzogc3RyaW5nIH07XG4gIEJsb2Nrczoge1xuICAgIHBhZ2U6IFtQYWdlXTtcbiAgICBjb2xsZWN0aW9uOiBbQ29sbGVjdGlvbl07XG4gIH07XG59PiB7XG4gIEBzZXJ2aWNlIGRlY2xhcmUgcm91dGVyOiBSb3V0ZXJTZXJ2aWNlO1xuXG4gIGdldCBhY3RpdmVDbGFzcygpIHtcbiAgICByZXR1cm4gdGhpcy5hcmdzLmFjdGl2ZUNsYXNzID8_ICdhY3RpdmUnO1xuICB9XG5cbiAgaXNBY3RpdmUgPSAoc3ViUGF0aDogc3RyaW5nKSA9PiB7XG4gICAgaWYgKHN1YlBhdGggPT09ICcvJykgcmV0dXJuIGZhbHNlO1xuXG4gICAgcmV0dXJuIHRoaXMucm91dGVyLmN1cnJlbnRVUkw_LnN0YXJ0c1dpdGgoc3ViUGF0aCk7XG4gIH07XG5cbiAgPHRlbXBsYXRlPlxuICAgIDx1bD5cbiAgICAgIHt7I2lmIChpc0NvbGxlY3Rpb24gQGl0ZW0pfX1cbiAgICAgICAge3sjZWFjaCBAaXRlbS5wYWdlcyBhcyB8cGFnZXx9fVxuICAgICAgICAgIDxsaT5cbiAgICAgICAgICAgIHt7I2lmIChpc0NvbGxlY3Rpb24gcGFnZSl9fVxuICAgICAgICAgICAgICB7e3lpZWxkIHBhZ2UgdG89J2NvbGxlY3Rpb24nfX1cbiAgICAgICAgICAgIHt7L2lmfX1cblxuICAgICAgICAgICAgPFBhZ2VzIEBpdGVtPXt7cGFnZX19PlxuICAgICAgICAgICAgICA8OnBhZ2UgYXMgfHB8Pnt7eWllbGQgcCB0bz0ncGFnZSd9fTwvOnBhZ2U-XG4gICAgICAgICAgICAgIDw6Y29sbGVjdGlvbiBhcyB8Y3w-e3t5aWVsZCBjIHRvPSdjb2xsZWN0aW9uJ319PC86Y29sbGVjdGlvbj5cbiAgICAgICAgICAgIDwvUGFnZXM-XG4gICAgICAgICAgPC9saT5cbiAgICAgICAge3svZWFjaH19XG4gICAgICB7e2Vsc2V9fVxuICAgICAgICA8YVxuICAgICAgICAgIGhyZWY9e3tAaXRlbS5wYXRofX1cbiAgICAgICAgICBjbGFzcz17e2lmICh0aGlzLmlzQWN0aXZlIEBpdGVtLnBhdGgpIHRoaXMuYWN0aXZlQ2xhc3N9fVxuICAgICAgICA-e3t5aWVsZCBAaXRlbSB0bz0ncGFnZSd9fTwvYT5cbiAgICAgIHt7L2lmfX1cbiAgICA8L3VsPlxuICA8L3RlbXBsYXRlPlxufVxuIl0sIm5hbWVzIjpbXSwibWFwcGluZ3MiOiI7QUFBQSxPQUFPLGVBQWUscUJBQXFCO0FBQzNDLFNBQVMsT0FBTyxRQUFRLGlCQUFpQjtBQUV6QyxZQUFZLGlCQUFpQiw0QkFBNEI7QUFDekQsY0FBYyxVQUFVLEVBQUUsSUFBSSxRQUFRLDZCQUE2QjtBQUNuRSxZQUFZLG1CQUFtQixnQ0FBZ0M7QUFFL0QsT0FBTyxNQUFNLGdCQUFnQjtJQUMzQjs7OztHQUlDLEdBQ0QsU0FBUztJQUNUO1FBQ0U7Ozs7S0FJQyxHQUNELGNBQWMsTUFBTTs7SUFFdEI7UUFDRTs7Ozs7Ozs7Ozs7Ozs7Ozs7O0tBa0JDLEdBQ0QsT0FBTztRQUNQOzs7Ozs7Ozs7Ozs7Ozs7Ozs7S0FrQkMsR0FDRCxhQUFhOzs7SUFHZixDQUFDLFFBQVE7SUFBc0IsTUFBTSxZQUFZO0lBRWpEOzs7OztHQUtDLEdBQ0QsTUFBQTtRQUFBLFNBQVUsQ0FBQTs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7OztFQXNCVixDQUFBLEVBQUE7WUFBQSxXQUFBLElBQVc7WUFBWDtnQkFBQSxPQUFBLEtBQUEsU0FBQSxDQUFBLEVBQVc7WUFBRDtRQUFBO0lBQUEsQ0F0QkE7QUF1Qlo7QUFFQSxTQUFTLGFBQWEsSUFBRyxPQUFPLFVBQVUsR0FBRyxLQUFLO0lBQ2hELE9BQU8sV0FBVztBQUNwQjtBQUVBLElBQUEsQUFBTSxRQUFOLE1BQU0sY0FBYztJQUNsQjtRQUFRLE1BQU0sT0FBTztRQUFZLGNBQWMsTUFBTTs7SUFDckQ7UUFDRSxPQUFPO1FBQ1AsYUFBYTs7O0lBR2YsQ0FBQztJQUFnQixRQUFRLGNBQWM7SUFFdkMsSUFBSSxjQUFjO1FBQ2hCLE9BQU8sSUFBSSxDQUFDLElBQUksQ0FBQyxXQUFXLElBQUk7SUFDbEM7SUFFQSxXQUFXLENBQUMsVUFBUyxNQUFNO1FBQ3pCLElBQUksYUFBWSxLQUFLLE9BQU87UUFFNUIsT0FBTyxJQUFJLENBQUMsTUFBTSxDQUFDLFVBQVUsRUFBRSxXQUFXO0lBQzVDLEVBQUU7SUFFRixNQUFBO1FBQUEsU0FBVSxDQUFBOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O0VBc0JWLENBQUEsRUFBQTtZQUFBLFdBQUEsSUFBVztZQUFYO2dCQUFBLE9BQUEsS0FBQSxTQUFBLENBQUEsRUFBVztZQUFEO1FBQUE7SUFBQSxDQXRCQTtBQXVCWiJ9